import type { ComponentRef, ViewContainerRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import type { Observable } from 'rxjs';
import { map, startWith } from 'rxjs';

import { setInputs } from '@core/common-legacy';
import type { ViewFilterInput, ViewFilterInputDef } from '@core/views-types';

import type { InputComponentContext, InputService } from '../types';
import { InputRangeComponent } from './input-range.component';
import type { InputRangeFormValue, InputRangeSavedValue } from './types';

export class InputRangeService implements InputService {
  public savedToFormValue(input: ViewFilterInput<InputRangeSavedValue>): InputRangeFormValue {
    return input.value;
  }

  public getComponentState(input: ViewFilterInput<InputRangeSavedValue>) {
    return input.value;
  }

  public getInputControl(inputDef: ViewFilterInputDef): InputRangeComponent['inputControl'] {
    return new FormGroup(
      {
        lte: new FormControl<number | null>(null),
        gte: new FormControl<number | null>(null),
      },
      (control: InputRangeComponent['inputControl']) => {
        const { gte, lte } = control.getRawValue();
        if (lte === null && gte === null) {
          return { required: true };
        }

        if (lte && gte && gte > lte) {
          return { gteBiggerThanLte: true };
        }

        return null;
      },
    );
  }

  public createInputComponent(vcRef: ViewContainerRef, destroy$: Observable<void>) {
    const componentRef = vcRef.createComponent(InputRangeComponent);
    return componentRef;
  }

  public updateInputs(
    componentRef: ComponentRef<InputRangeComponent>,
    inputs: Partial<Pick<InputRangeComponent, 'inputControl' | 'state'>>,
  ) {
    setInputs(componentRef, inputs);
    componentRef.changeDetectorRef.detectChanges();
  }

  public getInputLabel(
    context: InputComponentContext,
    control: InputRangeComponent['inputControl'],
    inputState$: Observable<null>,
  ) {
    return control.valueChanges.pipe(
      startWith(control.value),
      map(value => {
        const { lte, gte } = value ?? {};
        return `${gte ?? ''} - ${lte ?? ''}`;
      }),
    );
  }
}
